import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { groupApplicationService } from '../../services/groupApplicationService';
import { hasAnyRole } from '../../utils/auth';

const GroupApplications = ({ currentUser }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewApproved, setReviewApproved] = useState(true);

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      let data = [];
      
      if (selectedTab === 0) {
        // All applications (for admin/coach/staff)
        if (hasAnyRole(['staff', 'coach'])) {
          data = await groupApplicationService.getAllApplications();
        } else {
          data = await groupApplicationService.getApplicationsByUser(currentUser.id);
        }
      } else if (selectedTab === 1) {
        // Applications needing team supervisor review
        if (hasAnyRole(['team_supervisor', 'coach', 'staff'])) {
          data = await groupApplicationService.getApplicationsNeedingTeamSupervisorReview();
        }
      } else if (selectedTab === 2) {
        // Applications needing coach/staff review
        if (hasAnyRole(['coach', 'staff'])) {
          data = await groupApplicationService.getApplicationsNeedingCoachStaffReview();
        }
      } else if (selectedTab === 3) {
        // Applications for teams supervised by current user
        if (hasAnyRole(['team_supervisor', 'coach', 'staff'])) {
          data = await groupApplicationService.getApplicationsForTeamsSupervisedBy(currentUser.id);
        }
      }
      
      setApplications(data);
      setError(null);
    } catch (err) {
      setError('Failed to load applications: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, currentUser?.id]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleReview = (application) => {
    setSelectedApplication(application);
    setReviewNotes('');
    setReviewApproved(true);
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    try {
      if (selectedTab === 1) {
        // Team supervisor review
        await groupApplicationService.reviewByTeamSupervisor(
          selectedApplication.id,
          currentUser.id,
          reviewApproved,
          reviewNotes
        );
      } else if (selectedTab === 2) {
        // Coach/staff review
        await groupApplicationService.reviewByCoachStaff(
          selectedApplication.id,
          currentUser.id,
          reviewApproved,
          reviewNotes
        );
      }
      
      setReviewDialogOpen(false);
      loadApplications();
    } catch (err) {
      setError('Failed to submit review: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'TEAM_SUPERVISOR_APPROVED':
        return 'info';
      case 'TEAM_SUPERVISOR_REJECTED':
        return 'error';
      case 'PRIMARY_CAREGIVER_APPROVED':
        return 'success';
      case 'PRIMARY_CAREGIVER_REJECTED':
        return 'error';
      case 'COACH_STAFF_APPROVED':
        return 'success';
      case 'COACH_STAFF_REJECTED':
        return 'error';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pending Review';
      case 'TEAM_SUPERVISOR_APPROVED':
        return 'Team Supervisor Approved';
      case 'TEAM_SUPERVISOR_REJECTED':
        return 'Team Supervisor Rejected';
      case 'PRIMARY_CAREGIVER_APPROVED':
        return 'Primary Caregiver Approved';
      case 'PRIMARY_CAREGIVER_REJECTED':
        return 'Primary Caregiver Rejected';
      case 'COACH_STAFF_APPROVED':
        return 'Coach/Staff Approved';
      case 'COACH_STAFF_REJECTED':
        return 'Coach/Staff Rejected';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status;
    }
  };

  const canReview = (application) => {
    if (!currentUser) {
      return false;
    }
    
    if (selectedTab === 1) {
      // Team supervisor review - only if user is the team supervisor and hasn't reviewed yet
      return hasAnyRole(['team_supervisor', 'coach', 'staff']) && 
             application.groupId && 
             application.primaryUserId === currentUser.id &&
             application.teamSupervisorApproved === null;
    } else if (selectedTab === 2) {
      // Coach/staff review - only if user has coach/staff role and hasn't reviewed yet
      return hasAnyRole(['coach', 'staff']) && application.coachStaffApproved === null;
    }
    return false;
  };

  // Handle case where currentUser is null
  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">User not authenticated. Please log in again.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="primary">
        Team Applications
      </Typography>
      
      <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="All Applications" />
        {hasAnyRole(['team_supervisor', 'coach', 'staff']) && (
          <Tab label="Needing Team Supervisor Review" />
        )}
        {hasAnyRole(['coach', 'staff']) && (
          <Tab label="Needing Coach/Staff Review" />
        )}
        {hasAnyRole(['team_supervisor', 'coach', 'staff']) && (
                          <Tab label="Enrolled Programs" />
        )}
      </Tabs>

      {applications.length === 0 ? (
        <Alert severity="info">
          No applications found for the selected tab.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Applicant</TableCell>
                <TableCell>Team</TableCell>
                <TableCell>Household</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => (
                <TableRow key={application.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {application.userDisplayName || application.userName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {application.userEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {application.groupName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {application.groupTypeName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {application.householdName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {application.householdCode}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(application.status)} 
                      color={getStatusColor(application.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {application.reason}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(application.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleReview(application)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {canReview(application) && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => {
                                setSelectedApplication(application);
                                setReviewApproved(true);
                                setReviewNotes('');
                                setReviewDialogOpen(true);
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => {
                                setSelectedApplication(application);
                                setReviewApproved(false);
                                setReviewNotes('');
                                setReviewDialogOpen(true);
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Review Application - {selectedApplication?.userDisplayName || selectedApplication?.userName}
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Applicant</Typography>
                  <Typography variant="body2">
                    {selectedApplication.userDisplayName || selectedApplication.userName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedApplication.userEmail}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Team</Typography>
                  <Typography variant="body2">
                    {selectedApplication.groupName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedApplication.groupTypeName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Reason</Typography>
                  <Typography variant="body2">
                    {selectedApplication.reason}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Decision</InputLabel>
                    <Select
                      value={reviewApproved}
                      onChange={(e) => setReviewApproved(e.target.value)}
                      label="Decision"
                    >
                      <MenuItem value={true}>Approve</MenuItem>
                      <MenuItem value={false}>Reject</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Add any notes about your decision..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleReviewSubmit} 
            variant="contained"
            color={reviewApproved ? 'success' : 'error'}
          >
            {reviewApproved ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupApplications; 